<template>
    <div class="scrollable-pane-content" v-if="campaignMode === 'voice_broadcast'">
        <h2>{{t('campaign_edit.template.title')}}</h2>
        <table>
            <td>
                <table class="edit-table">
                    <tbody>
                    <tr>
                        <th>{{t('campaign_edit.template.contact_id')}}</th>
                        <td>
                            <input type="number" v-model.lazy="contactId">
                        </td>
                    </tr>
                    <tr>
                        <th>{{t('campaign_edit.template.template_label')}}</th>
                        <td class="static-width">
                            <textarea ref="templateTextArea" v-model="templateString" maxlength="1000"/>
                        </td>
                    </tr>
                    <tr>
                        <th>{{t('campaign_edit.template.sample_label')}}</th>
                        <td class="static-width">{{processedTemplate || previewResult}}</td>
                    </tr>
                    <tr v-show="enablePhase2Button">
                        <th><button class="row-edit-button"
                                    @click="phase2Template"
                        >{{t('campaign_edit.template.render_phase2')}}</button></th>
                    </tr>
                    <tr>
                        <th><button :disabled="!enableGenerateButton"
                                    class="row-edit-button"
                                    @click="generateTTS"
                        >{{t('campaign_edit.template.generate_tts')}}</button></th>
                        <td>
                            <audio v-if="soundFileUrl" controls :src="soundFileUrl" autoplay/>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </td>
            <td>
                <table>
                    <thead>
                    <tr>
                        <th>{{t('campaign_edit.template.contact_fields_title')}}</th>
                        <th>{{t('campaign_edit.template.contact_fields_token')}}</th>
                        <th>{{t('campaign_edit.template.contact_fields_value')}}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr @click="insertToText(value)" class="fields-table-tr" v-for="(key, value) in t('campaign_edit.template.contact_fields')">
                        <th>{{key}}</th>
                        <td>{{value}}</td>
                        <td>{{sampleContact && sampleContact[value]}}</td>
                    </tr>
                    </tbody>
                </table>
            </td>
        </table>
    </div>
</template>

<script>
    import axios from 'axios';
    import app from '../../App';
    import {t} from '../../i18n/i18n';
    import mustache from 'mustache';
    import MessagePopupView from '../../../controls/MessagePopupView';

    const STORAGE_KEY_SAMPLE_CONTACT_ID = 'sample_contact_id';

    mustache.escape = text => text;

    const phase2Regex = /\[\[(.+)]]/;

    export default {
        name: 'CampaignEditTemplatePage',
        data() {
            return {
                templateString: '',
                sampleContact: {},
                contactId: null,
                soundFileUrl: null,
                wrongTemplate: false,
                processedTemplate: '',
            }
        },
        methods: {
            async generateTTS() {
                if (!this.campaignId) {
                    return new MessagePopupView({addClass: 'warning', message: t('campaign_edit.template.warn_save_campaign'), queue: true}).show();
                }
                try {
                    const {data} = await axios.post(app.apiUrl('recordings/tts'), {
                        contact: Object.assign({
                            template: this.templateString,
                            campaign_id: this.campaignId
                        }, this.sampleContact),
                        text: this.processedTemplate || this.previewResult});
                    this.soundFileUrl = '/recordings/' + data.filename;
                } catch (err) {
                    console.error(err.request || err);
                }
            },
            async phase2Template() {

                const mustacheResult = this.previewResult,
                    sampleContact = this.sampleContact;

                if (!phase2Regex.test(mustacheResult))
                    return mustacheResult;

                const [fieldToSearch, fieldName, fields, projectId, campaignId, fieldToSearchSeparator=';', fieldNameSeparator=','] = mustacheResult.match(phase2Regex)[1]
                    .split(/\|/g);

                const contactsToFetch = sampleContact[fieldName] && sampleContact[fieldName].split(fieldToSearchSeparator).filter(x => x);

                if (!contactsToFetch) {
                    return console.log('no contacts to fetch');
                }

                const fieldsArray = fields.split(fieldNameSeparator);

                const contactRequests = contactsToFetch.map(fieldValue => {
                    const filtersObject = {
                        campaign_id: {value: campaignId}
                    };
                    filtersObject[fieldToSearch] = {value: fieldValue};
                    return axios.get(app.apiUrl(`projects/${projectId}/contacts`), {
                        params: {
                            filters: filtersObject,
                            fields: fieldsArray.join(',')
                        }
                    })
                });

                const allResults = await Promise.all(contactRequests);

                const joinFoundFields = allResults.map(({data:[contact]}) => contact &&
                    fieldsArray.map(field => contact[field]).join(' '));

                const joinResults = joinFoundFields.join(', ');

                this.processedTemplate = mustacheResult.replace(phase2Regex, joinResults);

            },
            insertToText(text) {

                const stringToAdd = `{{${text}}}`;
                const myField = this.$refs.templateTextArea;

                if (myField.selectionStart || myField.selectionStart === 0) {

                    const startPos = myField.selectionStart;
                    const endPos = myField.selectionEnd;

                    myField.value = myField.value.substring(0, startPos)
                        + stringToAdd
                        + myField.value.substring(endPos, myField.value.length);

                    myField.selectionStart = startPos + stringToAdd.length;
                    myField.selectionEnd = startPos + stringToAdd.length;

                } else {
                    myField.value += stringToAdd;
                }

                myField.focus();

            }
        },
        watch: {
            async contactId(newContact) {

                localStorage.setItem(STORAGE_KEY_SAMPLE_CONTACT_ID, newContact);
                this.soundFileUrl = null;

                if (!newContact) return;

                try {
                    const {data} =  await axios.get(app.apiUrl(`contacts/${newContact}`));
                    this.sampleContact = data
                } catch (err) {
                    if (err.request && err.request.status === 404) {
                        return this.sampleContact = null;
                    } else {
                        console.error(err.request || err)
                    }
                }

            }
        },
        computed: {
            contactLoaded() {
                return this.sampleContactFields && this.sampleContactFields.length
            },
            enablePhase2Button() {
                return phase2Regex.test(this.previewResult);
            },
            enableGenerateButton() {
                return this.templateString.length > 5 && !this.wrongTemplate
            },
            previewResult() {

                this.processedTemplate = '';

                if (!this.contactLoaded) {
                    this.wrongTemplate = true;
                    return t('campaign_edit.template.warn_no_contact')
                }

                try {

                    const template = this.templateString,
                        sampleContact = this.sampleContact;

                    const parsedTokens = mustache.parse(template);
                    const tokens = parsedTokens
                        .filter(([tokenType]) => tokenType === 'name')
                        .map(([,tokenName]) => tokenName);

                    const isMissingData = tokens.find(token => !sampleContact[token]);

                    if (isMissingData) return t('campaign_edit.template.warn_missing_data');

                    this.wrongTemplate = false;

                    return mustache.render(template, sampleContact);

                } catch (err) {
                    this.wrongTemplate = true;
                    return t('campaign_edit.template.warn_template_error', {err})
                }

            },
            sampleContactFields() {
                if (!this.sampleContact) return;
                return Object.values(this.sampleContact).filter(el => el);
            }

        },
        mounted() {
            this.contactId = localStorage.getItem(STORAGE_KEY_SAMPLE_CONTACT_ID);
        }
    }
</script>

<style scoped>
    .fields-table-tr:hover {
        background-color: #cccccc;
    }
    .static-width {
        width: 250px;
    }
    textarea {
        height: 150px;
    }
    audio {
        width: 250px;
        height: 24px;

    }
</style>